import React, { useEffect, useRef, useState } from "react";
import SinglePageContainer from "../../../../../../Common/SinglePageContainer";
import { Button, Form, message } from "antd";
import MeetingForm from "../Form";
import { useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import { useMeetingCreate } from "../../../../../../../hooks/api/meetings";
import { useVaadGetById } from "../../../../../../../hooks/api/vaads";
import { useMeetingTableGet } from "../../../../../../../hooks/api/meetings";
import { useVaadTabs } from "../../context";
import { RecordingAttachment } from "../Form/AttachmentsRecordings";
import { ChartAttachment } from "../Form/AttachmentsCharts";
import { parseDefaultTime } from "../../../../../../../utils";

export default function CreateMeetingContent() {
  const { id: vaadId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const meetingCreate = useMeetingCreate();
  const { setActiveTab, setMeetingsSubTab } = useVaadTabs();
  const vaadGet = useVaadGetById(vaadId);
  const meetingsGet = useMeetingTableGet();

  const [currentReadings, setCurrentReadings] = useState<any[]>([]);
  const [currentRecordings, setCurrentRecordings] = useState<
    RecordingAttachment[]
  >([]);
  const [currentCharts, setCurrentCharts] = useState<ChartAttachment[]>([]);

  useEffect(() => {
    meetingsGet.fetch({ vaadId, page: 1, pageSize: 1 });
  }, [vaadId]);

  const defaultsInitialized = useRef(false);
  useEffect(() => {
    if (defaultsInitialized.current) return;
    if (vaadGet.data && meetingsGet.data) {
      defaultsInitialized.current = true;
      const vaad = vaadGet.data;
      const meetingCount = meetingsGet.data.total || 0;
      const nextMeetingNumber = meetingCount + 1;
      const middahName = vaad.middah?.name || "Unknown";
      const vaadIdShort = vaad.vaadId || "WFM";
      const defaultName = `${vaadIdShort} ${middahName} meeting ${nextMeetingNumber}`;

      // Start Time defaults to the vaad's "Meetings default date" time,
      // End Time is one hour later. defaultTime may arrive as a 12-hour string
      // ("7:15 PM"), a 24-hour string ("16:10"), or an object { hour, minute }.
      const parsed = parseDefaultTime(vaad.defaultTime);
      const startTimeObj = parsed
        ? dayjs().hour(parsed.hour).minute(parsed.minute).second(0)
        : undefined;
      const endTimeObj = startTimeObj ? startTimeObj.add(1, "hour") : undefined;

      form.setFieldsValue({
        name: defaultName,
        country: vaad.country || "",
        state: vaad.state || "",
        city: vaad.city || "",
        address: vaad.address || "",
        startTime: startTimeObj,
        endTime: endTimeObj,
      });
    }
  }, [vaadGet.data, meetingsGet.data, form]);

  const handleCancel = () => {
    setMeetingsSubTab("future");
    navigate(`/vaadim-list/${vaadId}/view`);
  };

  const handleSubmit = async () => {
    if (!vaadId) {
      message.error("Vaad ID is missing");
      return;
    }
    try {
      const values = await form.validateFields();

      const baseDate = values.date ? dayjs(values.date) : dayjs();
      const startTime = values.startTime ? dayjs(values.startTime) : dayjs();
      const endTime = values.endTime ? dayjs(values.endTime) : dayjs();

      // The picked date/time is wall-clock time at the meeting location, so we
      // resolve the true UTC instant through the vaad's timezone. The tables
      // and form render the location's wall-clock via startTimeLocal/endTimeLocal.
      const timezone = vaadGet.data?.timezone || dayjs.tz.guess();
      const date = baseDate.format("YYYY-MM-DD");
      const toUtcIso = (time: dayjs.Dayjs) =>
        dayjs.tz(`${date} ${time.format("HH:mm:ss")}`, timezone).toISOString();

      const startDateTime = toUtcIso(startTime);
      const endDateTime = toUtcIso(endTime);

      const payload = {
        name: values.name,
        startTime: startDateTime,
        endTime: endDateTime,
        country: values.country,
        state: values.state,
        city: values.city,
        address: values.address,
        zoomLink: values.zoomLink,
        vaad: { id: vaadId },
        importFromLibraryReadings: values.selectedReadings?.map((r: any) => ({
          id: r.id,
        })),
        recordings: currentRecordings.map((r) => ({
          name: r.name,
          type: r.type,
          fileLink: r.fileLink,
          globalContent: false,
          middahs: [],
        })),
        charts: currentCharts.map((c) => ({
          name: c.name,
          type: c.type,
          fileLink: c.fileLink,
        })),
      };

      await meetingCreate.fetch(payload);
      message.success("Meeting created successfully");
      navigate(`/vaadim-list/${vaadId}/view`);
    } catch (error) {
      message.error("Failed to create meeting");
    }
  };

  return (
    <SinglePageContainer
      title="Add New Meeting"
      style={{ maxWidth: 792 }}
      cardLoading={vaadGet.loading}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <MeetingForm
          onReadingsChange={setCurrentReadings}
          onRecordingsChange={setCurrentRecordings}
          onChartsChange={setCurrentCharts}
        />
      </Form>
      <div style={{ display: "flex", justifyContent: "end", gap: 12 }}>
        <Button type="primary" ghost onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={meetingCreate.loading}
        >
          Save
        </Button>
      </div>
    </SinglePageContainer>
  );
}
